import { useQuery } from '@tanstack/react-query';

// ── Types ────────────────────────────────────────────────

export interface ApiVersionInfo {
  version: string;
  buildDate: string | null;
}

export interface BuildVersionInfo {
  /** process.env.REACT_APP_BUILD_VERSION baked in at build time, or "dev" locally. */
  uiVersion: string;
  /** GET /version's `version` field, or null before it resolves / on any failure. */
  apiVersion: string | null;
}

// ── Fetch (plain fetch — NOT the api.ts axios instance) ────
//
// api.ts's request interceptor attempts silent MSAL token acquisition on every call.
// /api/version is deliberately anonymous (see backend VersionController) and must stay
// readable even when auth is broken, so this hook talks to it directly.

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

function isApiVersionInfo(value: unknown): value is ApiVersionInfo {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).version === 'string'
  );
}

async function fetchApiVersion(): Promise<ApiVersionInfo> {
  // Manual AbortController rather than AbortSignal.timeout — the static isn't available
  // in the jsdom test environment (and older browsers), where it throws before fetch runs.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${API_BASE_URL}/version`, { signal: controller.signal });

    if (!response.ok) {
      throw new Error(`GET /version returned ${response.status}`);
    }

    const data: unknown = await response.json();
    if (!isApiVersionInfo(data)) {
      throw new Error('GET /version returned an unexpected shape');
    }

    return {
      version: data.version,
      buildDate: typeof data.buildDate === 'string' ? data.buildDate : null,
    };
  } finally {
    clearTimeout(timer);
  }
}

// ── Hook ─────────────────────────────────────────────────

/**
 * UI version comes from the baked-in env var (no request needed); API version is fetched
 * once per session and shared across every VersionFooter mount via the react-query cache.
 * Any failure (network error, timeout, non-2xx, malformed JSON) is swallowed — the caller
 * just sees apiVersion: null, matching the footer's "no error surfaced" requirement.
 */
export function useBuildVersion(): BuildVersionInfo {
  const uiVersion = process.env.REACT_APP_BUILD_VERSION || 'dev';

  const { data } = useQuery({
    queryKey: ['version', 'api'],
    queryFn: fetchApiVersion,
    retry: false,
    staleTime: Infinity,
  });

  return {
    uiVersion,
    apiVersion: data?.version ?? null,
  };
}
